import * as z from 'zod';

import { Timestamp } from '../db/firestore';

/**
 * Data modeling to represent a count of user edits (comments, changes on space, edits on a space, etc)
 */
export const UserContributionSchema = z.object({
    updatedAt: z.instanceof(Timestamp),
    count: z.number(),
});
export type UserContribution = z.infer<typeof UserContributionSchema>;

export const UserContributionsSchema = z.record(z.string(), UserContributionSchema);
export type UserContributions = z.infer<typeof UserContributionsSchema>;

/**
 * Create the initial set of contributions.
 */
export function createContributions(
    input: string[] | string = [],
    date: Date = new Date()
): UserContributions {
    const users = Array.isArray(input) ? input : [input];
    const contributions: UserContributions = {};

    users.filter(Boolean).forEach((user) => {
        contributions[user] = {
            count: 1,
            updatedAt: Timestamp.fromDate(date),
        };
    });

    return contributions;
}

/**
 * Add an user contributions on an object.
 */
export function addUserContribution(
    contributions: UserContributions,
    userId: string,
    opts?: { count?: number; updatedAt?: Timestamp }
): UserContributions {
    const updatedAt = opts?.updatedAt || Timestamp.now();
    const prevUpdatedAt = contributions[userId]?.updatedAt;

    return {
        ...contributions,
        [userId]: {
            count: (contributions[userId]?.count || 0) + (opts?.count || 1),
            updatedAt:
                prevUpdatedAt && prevUpdatedAt.toMillis() > updatedAt.toMillis()
                    ? prevUpdatedAt
                    : updatedAt,
        },
    };
}

/**
 * List users who made contributions (ordered by count).
 */
export function listUsersInContributions(contributions: UserContributions): string[] {
    return Object.entries(contributions)
        .sort((a, b) => {
            if (a[1].count < b[1].count) {
                return -1;
            }
            if (a[1].count > b[1].count) {
                return 1;
            }
            return a[1].updatedAt < b[1].updatedAt ? -1 : 1;
        })
        .map((a) => a[0]);
}

/**
 * Get last contribution (date and user).
 */
export function getLastContribution(
    contributions: UserContributions
): { user: string; updatedAt: Timestamp } | null {
    return getHeadContribution(contributions, (a, b) =>
        a.updatedAt.toMillis() > b.updatedAt.toMillis() ? -1 : 1
    );
}

/**
 * Get first contribution (date and user)
 */
export function getFirstContribution(
    contributions: UserContributions
): { user: string; updatedAt: Timestamp } | null {
    return getHeadContribution(contributions, (a, b) =>
        a.updatedAt.toMillis() > b.updatedAt.toMillis() ? 1 : -1
    );
}

/**
 * Get the head contribution, defined by a custom sort function.
 */
function getHeadContribution(
    contributions: UserContributions,
    sort: (a: UserContribution, b: UserContribution) => number
): { user: string; updatedAt: Timestamp } | null {
    if (!contributions) {
        return null;
    }

    const result = Object.entries(contributions).sort((a, b) => sort(a[1], b[1]))[0];

    if (!result) {
        return null;
    }

    return {
        user: result[0],
        updatedAt: result[1].updatedAt,
    };
}

/**
 * Count the total number of contributions.
 */
export function countAllContributions(contributions: UserContributions): number {
    return Object.entries(contributions).reduce(
        (accumulator, currentValue) => accumulator + currentValue[1].count,
        0
    );
}

/**
 * Merge and accumulate all contributions.
 */
export function mergeContributions(a: UserContributions, b: UserContributions): UserContributions {
    const result = {
        ...a,
    };

    Object.keys(b).forEach((userId) => {
        const current = b[userId];

        const existing = result[userId];
        if (existing) {
            result[userId] = {
                count: existing.count + current.count,
                updatedAt:
                    current.updatedAt > existing.updatedAt ? current.updatedAt : existing.updatedAt,
            };
        } else {
            result[userId] = current;
        }
    });

    return result;
}
